import axiosIns from '@axiosIns'
import axios from 'axios'
import Vue from 'vue'

const DEFAULT_CURRENT_PAGE = 1
const DEFAULT_PAGE_SIZE = 50
let cancelTokenSourceDetails = null
let cancelTokenSourceList = null

const cancelPendingDetailRequests = () => {
  if (cancelTokenSourceDetails) {
    cancelTokenSourceDetails.cancel()
  }
  cancelTokenSourceDetails = axios.CancelToken.source()
}

const cancelPendingListRequests = () => {
  if (cancelTokenSourceList) {
    cancelTokenSourceList.cancel()
  }
  cancelTokenSourceList = axios.CancelToken.source()
}

export default {
  namespaced: true,
  state: {
    activeYoungId: null,
    activeYoungDetails: null,
    messages: [],
    youngs: [],
    blockedYoungs: [],
    LastContacts: [],
    youngsComment: [],
    blockedBy: {},
    excelDatas: null,
    zipDatas: null,
    pagination: {
      currentPage: DEFAULT_CURRENT_PAGE,
      pageSize: DEFAULT_PAGE_SIZE,
      hasNext: false,
      hasPrevious: false,
    },
    searchParams: {
      searchInput: '',
      filterByMissedCalls: false,
      filterByPendingConversation: false,
    },
    isInitialLoad: true,
  },
  getters: {
    getLastContact: state => id => state.LastContacts.find(lastContact => lastContact.id === id),
    activeYoung: (state, getters) => {
      if (state.activeYoungDetails) {
        return state.activeYoungDetails
      }
      return state.activeYoungId
        ? getters.youngs.find(y => y.id === state.activeYoungId) || {}
        : {}
    },
    messages: (state, getters) => state.messages
        .map(message => ({
          ...message,
          user: message.userId ? getters.getLastContact(message.userId) : null,
        }))
        .sort((a, b) => new Date(a.date) - new Date(b.date)),
    youngsComment: state => state.youngsComment,
    youngs: (state, getters) => {
      const processedYoungs = state.youngs.map(young => ({
        ...young,
        lastMessageUser: young.lastMessageUserId
          ? getters.getLastContact(young.lastMessageUserId)
          : null,
      }))
      // Le tri est maintenant géré par l'API lorsque filterByMissedCalls est false.
      // Si filterByMissedCalls est true, l'API fournit déjà le bon ordre.
      return processedYoungs
    },
    blockedYoungs: (state, getters) => {
      const processedYoungs = state.blockedYoungs.map(young => ({
        ...young,
        lastMessageUser: young.lastMessageUserId
          ? getters.getLastContact(young.lastMessageUserId)
          : null,
      }))
      // Le tri est maintenant géré par l'API lorsque filterByMissedCalls est false.
      // Si filterByMissedCalls est true, l'API fournit déjà le bon ordre.
      return processedYoungs
    },
    getBlockedBy: state => state.blockedBy,
    excelDatas: state => state.excelDatas,
    zipDatas: state => state.zipDatas,
    pagination: state => state.pagination,
    searchParams: state => state.searchParams,
  },
  mutations: {
    addOrUpdateMessage(state, message) {
      if (state.activeYoungId === message.youngId) {
        state.messages = state.messages
          .filter(m => m.id !== message.id)
          .concat([message])
      }
      const youngIndex = state.youngs.findIndex(y => y.id === message.youngId)
      if (youngIndex >= 0) {
        const young = state.youngs[youngIndex]
        // SMS (type=1) or WhatsApp (type=2) from a Young -> pending; from a User → not pending; calls leave it unchanged
        const isSmsOrWhatsApp = message.type === 1 || message.type === 2
        const hasPendingMessage = isSmsOrWhatsApp
          ? message.senderType === 1
          : young.hasPendingMessage
        Vue.set(state.youngs, youngIndex, {
          ...young,
          lastMessageDate: message.date,
          lastActivityDate: message.date,
          lastMessageUserId: message.userId || young.lastMessageUserId,
          hasPendingMessage,
          unreadMessages: (message.senderType === 1 && state.activeYoungId !== message.youngId)
            ? (young.unreadMessages || 0) + 1
            : (young.unreadMessages || 0),
        })
        // Move young to top of list when a new inbound message arrives
        // Only in default sort mode (not when using special filters)
        const isInDefaultMode = !state.searchParams.filterByMissedCalls
          && !state.searchParams.filterByPendingConversation
        if (isInDefaultMode && message.senderType === 1) {
          const updatedYoung = state.youngs[youngIndex]
          state.youngs.splice(youngIndex, 1)
          state.youngs.unshift(updatedYoung)
        }
      }
    },
    addOrUpdateYoung(state, young) {
      const index = state.youngs.findIndex(y => y.id === young.id)
      if (index >= 0) {
        // Garde la position du jeune dans la liste
        Vue.set(state.youngs, index, { ...state.youngs[index], ...young })
      } else {
        state.youngs.unshift(young)
      }

      if (state.activeYoungId === young.id && state.activeYoungDetails) {
        state.activeYoungDetails = { ...state.activeYoungDetails, ...young }
      }
    },
    deleteMessage(state, id) {
      state.messages = state.messages.filter(m => m.id !== id)
    },
    deleteYoung(state, id) {
      if (state.activeYoungId === id) {
        state.activeYoungId = null
      }
      state.youngs = state.youngs.filter(y => y.id !== id)
      state.messages = state.messages.filter(m => m.youngId !== id)
    },
    setActiveYoungId(state, id) {
      state.activeYoungId = id
      state.messages = state.messages.filter(m => m.youngId === id)
      // Clear unread counter when the conversation is opened
      const young = state.youngs.find(y => y.id === id)
      if (young) {
        Vue.set(young, 'unreadMessages', 0)
      }
    },
    updateActiveYoungDetails(state, youngDetails) {
      state.activeYoungDetails = youngDetails
    },
    replaceMessages(state, messages) {
      // Fully replace messages for the active young used after permission changes (e.g. take charge)
      // Keeps cached messages for other youngs intact
      state.messages = [
        ...state.messages.filter(m => m.youngId !== state.activeYoungId),
        ...messages,
      ]
    },
    updateMessages(state, messages) {
      const allMessages = state.messages
        .filter(m => m.youngId === state.activeYoungId)
        .concat(messages)
      const distinctMessages = []
      const idMap = new Map()
      allMessages.forEach(message => {
        if (!idMap.has(message.id)) {
          idMap.set(message.id, true)
          distinctMessages.push(message)
        }
      })
      state.messages = distinctMessages
    },
    updateYoungs(state, youngs) {
      state.youngs = youngs
    },
    appendYoungs(state, youngs) {
      // Append new youngs to the existing array, avoiding duplicates
      const youngIds = new Set(state.youngs.map(y => y.id))
      const newYoungs = youngs.filter(y => !youngIds.has(y.id))
      state.youngs = [...state.youngs, ...newYoungs]
    },
    updatePagination(state, { responseDataLength, pageNumber, pageSize }) {
      state.pagination.currentPage = pageNumber
      state.pagination.pageSize = pageSize
      state.pagination.hasNext = responseDataLength === pageSize && responseDataLength > 0
      state.pagination.hasPrevious = pageNumber > 1
    },
    updateSearchParams(state, params) {
      state.searchParams = {
        searchInput:
          params.searchInput !== undefined
            ? params.searchInput
            : state.searchParams.searchInput,
        filterByMissedCalls:
          params.filterByMissedCalls !== undefined
            ? params.filterByMissedCalls
            : state.searchParams.filterByMissedCalls,
        filterByPendingConversation:
          params.filterByPendingConversation !== undefined
            ? params.filterByPendingConversation
            : state.searchParams.filterByPendingConversation,
      }
    },
    changeStatusToPending(state, id) {
      const foundIndex = state.youngs.findIndex(x => x.id === id)
      if (foundIndex >= 0) {
        Vue.set(state.youngs, foundIndex, { ...state.youngs[foundIndex], conversationStatus: 1, whoTakesCareUserId: null })
      }
      if (state.activeYoungDetails && state.activeYoungDetails.id === id) {
        state.activeYoungDetails = { ...state.activeYoungDetails, conversationStatus: 1, whoTakesCareUserId: null }
      }
    },
    updateConversationStatus(state, { youngId, conversationStatus, userId }) {
      const foundIndex = state.youngs.findIndex(x => x.id === youngId)
      if (foundIndex >= 0) {
        Vue.set(state.youngs, foundIndex, {
          ...state.youngs[foundIndex],
          conversationStatus,
          whoTakesCareUserId: userId,
          // An assigned agent means the conversation is no longer "pending SMS" per the filter definition
          hasPendingMessage: userId ? false : state.youngs[foundIndex].hasPendingMessage,
        })
      }
      if (state.activeYoungDetails && state.activeYoungDetails.id === youngId) {
        state.activeYoungDetails = {
          ...state.activeYoungDetails,
          conversationStatus,
          whoTakesCareUserId: userId,
        }
      }
    },
    fetchedYoungComments(state, comments) {
      state.youngsComment = comments
    },
    fetchedBlockedYoungs(state, blockedYoungs) {
      state.blockedYoungs = blockedYoungs
    },
    updatedComment() {},
    updateYoung(state, updatedYoung) {
      state.youngs = state.youngs.map(young => (young.id === updatedYoung.id ? { ...young, ...updatedYoung } : young))

      if (state.activeYoungId === updatedYoung.id && state.activeYoungDetails) {
        state.activeYoungDetails = { ...state.activeYoungDetails, ...updatedYoung }
      }
    },
    addedComment(state, comment) {
      state.youngsComment.push(comment)
    },
    fetchedBlockedBy(state, blockedBy) {
      state.blockedBy = blockedBy
    },
    updateExcelDatas(state, excelDatas) {
      state.excelDatas = excelDatas
    },
    updateZipDatas(state, zipDatas) {
      state.zipDatas = zipDatas
    },
    fetchLastContacts(state, LastContacts) {
      state.LastContacts = LastContacts
    },
    clearYoungs(state) {
      state.youngs = []
      state.pagination.currentPage = DEFAULT_CURRENT_PAGE
      state.pagination.hasNext = false
    },
    setInitialLoadStatus(state, status) {
      state.isInitialLoad = status
    },
  },
  actions: {
    getYoungIdsOrderedByLastMissedCall() {
      return new Promise((resolve, reject) => {
        axiosIns
          .get('Young/GetYoungIdsOrderedByLastMissedCall')
          .then(response => {
            resolve(response.data)
          })
          .catch(error => reject(error))
      })
    },
    addOrUpdateMessage({ commit }, message) {
      commit('addOrUpdateMessage', message)
    },
    addOrUpdateYoung({ commit }, young) {
      commit('addOrUpdateYoung', young)
    },
    deleteYoung({ commit }, youngId) {
      return new Promise((resolve, reject) => {
        axiosIns
          .delete(`Young/${youngId}`)
          .then(response => {
            commit('deleteYoung', youngId)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    setActiveYoungId({ commit }, youngId) {
      return new Promise((resolve, reject) => {
        cancelPendingDetailRequests()
        commit('setActiveYoungId', youngId)

        Promise.all([
          axiosIns.get(`Young/${youngId}`, {
            cancelToken: cancelTokenSourceDetails.token,
          }),
          axiosIns.get(`Young/${youngId}/Messages`, {
            cancelToken: cancelTokenSourceDetails.token,
          }),
        ])
          .then(([youngDetailsResponse, messagesResponse]) => {
            commit('updateActiveYoungDetails', youngDetailsResponse.data)
            commit('replaceMessages', messagesResponse.data)
            resolve({ young: youngDetailsResponse.data, messages: messagesResponse.data })
          })
          .catch(error => reject(error))
      })
    },
    fetchYoungs({ commit, state }, params = {}) {
      return new Promise((resolve, reject) => {
        cancelPendingListRequests()

        const pageNumber = params.page || 1
        const pageSize = params.pageSize || state.pagination.pageSize

        let apiSearchInput = ''
        let apiFilterByMissedCalls = false
        let apiFilterByPendingConversation = false

        if (pageNumber === 1) {
          apiSearchInput = params.searchQuery !== undefined ? params.searchQuery : ''
          apiFilterByMissedCalls = params.isFilteringByAbsencesCalls !== undefined
              ? params.isFilteringByAbsencesCalls
              : false
          apiFilterByPendingConversation = params.isFilteringByPendingConversation !== undefined
              ? params.isFilteringByPendingConversation
              : false

          commit('updateSearchParams', {
            searchInput: apiSearchInput,
            filterByMissedCalls: apiFilterByMissedCalls,
            filterByPendingConversation: apiFilterByPendingConversation,
          })
          commit('clearYoungs')
        } else {
          apiSearchInput = state.searchParams.searchInput
          apiFilterByMissedCalls = state.searchParams.filterByMissedCalls
          apiFilterByPendingConversation = state.searchParams.filterByPendingConversation
        }

        const queryParams = new URLSearchParams({
          pageNumber,
          pageSize,
          searchInput: apiSearchInput,
          filterByMissedCalls: apiFilterByMissedCalls,
          filterByPendingConversation: apiFilterByPendingConversation,
          detailed: false,
        })

        axiosIns
          .get(`Young?${queryParams.toString()}`, {
            cancelToken: cancelTokenSourceList.token,
          })
          .then(response => {
            commit('updatePagination', {
              responseDataLength: response.data.length,
              pageNumber,
              pageSize,
            })

            if (pageNumber === 1) {
              commit('updateYoungs', response.data)
            } else {
              commit('appendYoungs', response.data)
            }
            resolve(response)
          })
          .catch(error => {
            if (!axios.isCancel(error)) {
              reject(error)
            } else {
              resolve({ data: [] })
            }
          })
      })
    },
    fetchBlockedYoungs({ commit }) {
      return new Promise((resolve, reject) => {
        axiosIns
          .get('Young/blocked')
          .then(response => {
            commit('fetchedBlockedYoungs', response.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    changeConversationStatus({ commit }, youngId) {
      return new Promise((resolve, reject) => {
        axiosIns
          .put(`Young/${youngId}/PendingConversation`)
          .then(response => {
            commit('changeStatusToPending', youngId)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    fetchYoungComments({ commit }, youngId) {
      return new Promise((resolve, reject) => {
        axiosIns
          .get(`Young/${youngId}/Comments`)
          .then(response => {
            commit('fetchedYoungComments', response.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    updateComment({ commit }, data) {
      const body = {
        comment: data.comment,
        commentId: data.commentId,
      }
      return new Promise((resolve, reject) => {
        axiosIns
          .put(`Young/${data.youngId}/UpdateComment`, body)
          .then(response => {
            commit('updatedComment', response.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    addComment({ commit }, data) {
      return new Promise((resolve, reject) => {
        axiosIns
          .post(`Young/${data.youngId}/AddComment`, data.comment, {
            headers: { 'Content-Type': 'application/json' },
          })
          .then(response => {
            commit('addedComment', response.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    updateYoungDetails({ commit }, data) {
      return new Promise((resolve, reject) => {
        axiosIns
          .put('Young', data)
          .then(response => {
            commit('updateYoung', data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    fetchBlockedBy({ commit }, youngId) {
      return new Promise((resolve, reject) => {
        axiosIns
          .get(`Young/${youngId}/BlockedBy`)
          .then(response => {
            commit('fetchedBlockedBy', response.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    fetchExcelDatas({ commit }, youngId) {
      return new Promise((resolve, reject) => {
        axiosIns
          .post(`Young/${youngId}/ExportExcel`, {}, { responseType: 'blob' })
          .then(response => {
            commit('updateExcelDatas', response.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    fetchZipDatas({ commit }, youngId) {
      return new Promise((resolve, reject) => {
        axiosIns
          .post(`Young/${youngId}/ExportZip`, {}, { responseType: 'blob' })
          .then(response => {
            commit('updateZipDatas', response.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    fetchLastContacts({ commit }) {
      return new Promise((resolve, reject) => {
        axiosIns
          .get('Young/LastContacts')
          .then(response => {
            commit('fetchLastContacts', response.data)
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    sendAccommodationRequest(context, { youngId, accommodationRequestComment }) {
      return new Promise((resolve, reject) => {
        axiosIns
          .post(`MailSender/${youngId}`, accommodationRequestComment ? { accommodationRequestComment } : {})
          .then(response => {
            resolve(response)
          })
          .catch(error => reject(error))
      })
    },
    sendMessage(_, { youngId, body }) {
      return axiosIns.post('Message/Outgoing', { youngId, body })
    },
    openConversation({ commit, dispatch, rootGetters }, youngId) {
      return axiosIns.post(`User/OpenConversation/${youngId}`).then(response => {
        const currentUser = rootGetters['users/currentUser']
        commit('updateConversationStatus', {
          youngId,
          conversationStatus: 2,
          userId: currentUser?.id || null,
        })
        // Reload messages now that we're the handler (backend now allows SMS/WhatsApp access)
        return dispatch('setActiveYoungId', youngId).then(() => response)
      })
    },
    closeConversation({ commit }, youngId) {
      return axiosIns.put(`User/CloseConversation/${youngId}`).then(response => {
        commit('updateConversationStatus', {
          youngId,
          conversationStatus: 3,
          userId: null,
        })
        return response
      })
    },
  },
}
